import { ISkillNode } from "@skills-app/models/skillNode";
import IUserSkill from "@skills-app/models/userSkills";
import {
  SearchBySkillOrConsultantPageActions,
  SearchBySkillOrConsultantApiActions,
  SkillSearchBarActions,
  ConsultantAnyFilterActions,
  ConsultantNameFilterActions,
  MyProfilePageActions,
  AddUserSkillDialogueActions,
  ConsultantRoleFilterActions,
  PrimaryCapabilitySearchBarActions,
  IndustrySearchBarActions,
} from "../actions";
import {
  on,
  createReducer,
  createFeatureSelector,
  createSelector,
} from "@ngrx/store";
import { getSelectedSkillProficiencyRange } from "@skills-app/skill/reducers/skill.reducer";
import SkillRating, {
  getSkillRatingIndex,
} from "@skills-app/models/skillRating";
import ISkillNodeWithRating from "@skills-app/models/skillNodeWithRating";
import {
  AddSkillToProfileActions,
  CertificationSearchBarActions,
} from "@skills-app/shared/actions";
import ICertification from "@skills-app/models/certification";
import { SkillChampionPageActions } from "@skills-app/thingspace/actions";
import { getSelectedConsultants } from "@skills-app/thingspace/reducers/skill-champion.reducer";
import IOrgNode from "@skills-app/models/orgNode";
import {
  RelatedAndAliasedSkillsActions,
  SkillApiActions,
} from "@skills-app/skill/actions";
import { BuildTeamPageActions } from "@skills-app/build-team/actions";
import { ICapability } from "@skills-app/models/capability";
import { SkillProficiencyRange } from "@skills-app/models/skillProficiencyRange";
import { IIndustryItem } from "@skills-app/models/industry";

export interface IConsultantSearchLoading {
  readonly filteredConsultants: boolean;
  readonly AllUserRoles: boolean;
  readonly csvGenerating: boolean;
  readonly addUserSkill: boolean;
}

const initialLoadingState: IConsultantSearchLoading = {
  filteredConsultants: false,
  AllUserRoles: false,
  csvGenerating: false,
  addUserSkill: false,
};

export interface IConsultantSearchState {
  loading: IConsultantSearchLoading;
  filteredConsultants: IUserSkill[];
  selectedSkills: ISkillNode[];
  isSkillComboLogicOR: boolean;
  isIndustryComboLogicOR: boolean;
  selectedSkillProficiencyRange: SkillProficiencyRange;
  selectedCapabilities: ICapability[];
  selectedCertifications: ICertification[];
  selectedOrganisations: IOrgNode[];
  selectedLocations: string[];
  selectedRoles: string[];
  anySearchTerm: string;
  consultantNameSearchTerm: string;
  showUnknownProficiency: boolean;
  showIncludeRelatedSkills: boolean;
  includeRelatedSkills: boolean;
  showIncludeExpiredCerts: boolean;
  includeExpiredCerts: boolean;
  hideNoProfile: boolean;
  AllUserRoles: string[];
  AllOrgs: IOrgNode[];
  allOrgs: IOrgNode[];
  allLocations: string[];
  relatedSelectedSkills: { skillId: string; relatedSkills: ISkillNode[] }[];
  selectedIndustryItems: IIndustryItem[];
  updatedWithinMonths: number | null;
  lobSelectionReadyVersion: number;
}

const initialState: IConsultantSearchState = {
  loading: initialLoadingState,
  filteredConsultants: [],
  selectedSkills: [],
  isSkillComboLogicOR: true,
  isIndustryComboLogicOR: true,
  selectedSkillProficiencyRange: { min: 2, max: 6 },
  selectedCapabilities: [],
  selectedCertifications: [],
  selectedOrganisations: [],
  selectedRoles: [],
  selectedLocations: [],
  anySearchTerm: "",
  consultantNameSearchTerm: "",
  showUnknownProficiency: true,
  showIncludeRelatedSkills: true,
  includeRelatedSkills: false,
  showIncludeExpiredCerts: true,
  includeExpiredCerts: false,
  hideNoProfile: true,
  AllUserRoles: [],
  AllOrgs: [],
  allOrgs: [],
  relatedSelectedSkills: [],
  allLocations: [],
  selectedIndustryItems: [],
  updatedWithinMonths: null,
  lobSelectionReadyVersion: 0,
};

export const consultantSearchFeatureKey = "consultantSearch";

export const reducer = createReducer(
  initialState,

  on(SearchBySkillOrConsultantPageActions.loadPage, (state) => ({
    ...state,
    loading: { ...state.loading, filteredConsultants: false },
    lobSelectionReadyVersion: 0,
  })),

  on(
    SearchBySkillOrConsultantPageActions.unloadPage,
    SkillChampionPageActions.clearConsultantSearch,
    (state) => ({
      ...state,
      selectedSkills: [],
      selectedCertifications: [],
      selectedOrganisations: [],
      filteredConsultants: [],
      selectedRoles: [],
      selectedLocations: [],
      anySearchTerm: "",
      consultantNameSearchTerm: "",
      updatedWithinMonths: null,
      loading: { ...state.loading, filteredConsultants: false },
      lobSelectionReadyVersion: 0,
    }),
  ),

  on(BuildTeamPageActions.unloadPage, (state) => ({
    ...state,
    loading: { ...state.loading, filteredConsultants: false },
    lobSelectionReadyVersion: 0,
  })),

  on(SkillSearchBarActions.unloadComponent, (state) => ({
    ...state,
    selectedSkills: [],
    isSkillComboLogicOR: true,
  })),

  on(IndustrySearchBarActions.unloadComponent, (state) => ({
    ...state,
    selectedIndustryItems: [],
    isIndustryComboLogicOR: true,
  })),

  on(PrimaryCapabilitySearchBarActions.unloadComponent, (state) => ({
    ...state,
    selectedCapabilities: [],
  })),

  on(
    SearchBySkillOrConsultantApiActions.requestFilteredConsultants,
    (state) => ({
      ...state,
      loading: { ...state.loading, filteredConsultants: true },
    }),
  ),

  on(
    SearchBySkillOrConsultantApiActions.filterConsultantsSuccess,
    (state, action) => ({
      ...state,
      filteredConsultants: action.consultants.map((u) => ({
        ...u,
        Org: state.AllOrgs.find((s) => s.Id === u.OrgId),
      })),
      loading: { ...state.loading, filteredConsultants: false },
    }),
  ),

  on(SearchBySkillOrConsultantApiActions.filterConsultantsError, (state) => ({
    ...state,
    loading: { ...state.loading, filteredConsultants: false },
  })),

  on(
    SearchBySkillOrConsultantApiActions.requestFilteredConsultantsCSV,
    (state) => ({
      ...state,
      loading: { ...state.loading, csvGenerating: true },
    }),
  ),

  on(
    SearchBySkillOrConsultantApiActions.filteredConsultantsCSVSuccess,
    (state) => ({
      ...state,
      loading: { ...state.loading, csvGenerating: false },
    }),
  ),

  on(
    SearchBySkillOrConsultantApiActions.filteredConsultantsCSVError,
    (state) => ({
      ...state,
      loading: { ...state.loading, csvGenerating: false },
    }),
  ),

  on(
    SearchBySkillOrConsultantPageActions.filterConsultantsByRole,
    (state, action) => ({
      ...state,
      selectedRoles: action.roles,
    }),
  ),

  on(SearchBySkillOrConsultantPageActions.clearSelectedRoles, (state) => ({
    ...state,
    selectedRoles: [],
  })),

  on(ConsultantNameFilterActions.setFilter, (state, action) => ({
    ...state,
    consultantNameSearchTerm:
      action.filterString && action.filterString.length >= 3
        ? action.filterString
        : "",
  })),

  on(
    PrimaryCapabilitySearchBarActions.addPrimaryCapabilityToSearch,
    (state, action) => ({
      ...state,
      selectedCapabilities: state.selectedCapabilities.find(
        (x) => x.Id === action.capability.Id,
      )
        ? state.selectedCapabilities
        : [...state.selectedCapabilities, action.capability],
    }),
  ),

  on(ConsultantAnyFilterActions.setFilter, (state, action) => ({
    ...state,
    anySearchTerm:
      action.filterString && action.filterString.length >= 3
        ? action.filterString
        : "",
  })),

  on(
    SearchBySkillOrConsultantPageActions.setUpdatedWithinMonths,
    (state, action) => ({
      ...state,
      updatedWithinMonths: action.months,
    }),
  ),

  on(
    SkillSearchBarActions.addSkillToSearch,
    RelatedAndAliasedSkillsActions.addSkillToSearch,
    SearchBySkillOrConsultantPageActions.selectSkillObject,
    SkillChampionPageActions.setSkillForConsultantSearch,
    (state, action) => ({
      ...state,
      selectedSkills: state.selectedSkills.find((x) => x.Id === action.skill.Id)
        ? state.selectedSkills
        : [...state.selectedSkills, action.skill],
    }),
  ),

  on(SkillSearchBarActions.addAllSkillsToSearch, (state, action) => ({
    ...state,
    selectedSkills: action.skills,
  })),

  on(IndustrySearchBarActions.addIndustryItemToSearch, (state, action) => ({
    ...state,
    selectedIndustryItems: state.selectedIndustryItems.find(
      (x) => x.IndustryOptions === action.industryItem.IndustryOptions,
    )
      ? state.selectedIndustryItems
      : [...state.selectedIndustryItems, action.industryItem],
  })),

  on(ConsultantRoleFilterActions.addRoleToSearch, (state, action) => ({
    ...state,
    selectedRoles: state.selectedRoles.find((role) => role === action.role)
      ? state.selectedRoles
      : [...state.selectedRoles, action.role],
  })),

  on(SkillSearchBarActions.removeSkillFromSearch, (state, action) => ({
    ...state,
    selectedSkills: state.selectedSkills.filter(
      (skill) => skill.Id !== action.skill.Id,
    ),
    relatedSelectedSkills: state.relatedSelectedSkills.filter(
      (relatedSkill) => relatedSkill.skillId !== action.skill.Id,
    ),
  })),

  on(SkillSearchBarActions.removeAllSkillsFromSearch, (state) => ({
    ...state,
    selectedSkills: [],
    relatedSelectedSkills: [],
  })),

  on(
    IndustrySearchBarActions.removeIndustryItemFromSearch,
    (state, action) => ({
      ...state,
      selectedIndustryItems: state.selectedIndustryItems.filter(
        (industryItem) =>
          industryItem.IndustryOptions !== action.industryItem.IndustryOptions,
      ),
    }),
  ),

  on(SkillSearchBarActions.changeSkillComboLogic, (state) => ({
    ...state,
    isSkillComboLogicOR: !state.isSkillComboLogicOR,
  })),

  on(SkillSearchBarActions.resetSkillComboLogic, (state) => ({
    ...state,
    isSkillComboLogicOR: true,
  })),

  on(IndustrySearchBarActions.changeIndustryComboLogic, (state) => ({
    ...state,
    isIndustryComboLogicOR: !state.isIndustryComboLogicOR,
  })),

  on(IndustrySearchBarActions.resetIndustryComboLogic, (state) => ({
    ...state,
    isIndustryComboLogicOR: true,
  })),

  on(
    PrimaryCapabilitySearchBarActions.removePrimaryCapabilityFromSearch,
    (state, action) => ({
      ...state,
      selectedCapabilities: state.selectedCapabilities.filter(
        (capability) => capability.Id !== action.capability.Id,
      ),
    }),
  ),

  on(ConsultantRoleFilterActions.removeRoleFromSearch, (state, action) => ({
    ...state,
    selectedRoles: state.selectedRoles.filter((role) => role !== action.role),
  })),

  on(SearchBySkillOrConsultantPageActions.showUnknownProficiency, (state) => ({
    ...state,
    showUnknownProficiency: !state.showUnknownProficiency,
  })),

  on(SearchBySkillOrConsultantPageActions.showRelatedSkills, (state) => ({
    ...state,
    includeRelatedSkills: !state.includeRelatedSkills,
  })),

  on(SearchBySkillOrConsultantPageActions.resetShowRelatedSkills, (state) => ({
    ...state,
    includeRelatedSkills: false,
  })),

  on(SearchBySkillOrConsultantPageActions.showExpiredCerts, (state) => ({
    ...state,
    includeExpiredCerts: !state.includeExpiredCerts,
  })),

  on(SearchBySkillOrConsultantPageActions.toggleHideNoProfile, (state) => ({
    ...state,
    hideNoProfile: !state.hideNoProfile,
  })),

  on(
    SearchBySkillOrConsultantPageActions.setHideNoProfile,
    (state, action) => ({
      ...state,
      hideNoProfile: action.hide,
    }),
  ),

  on(SearchBySkillOrConsultantApiActions.requestRoles, (state) => ({
    ...state,
    loading: { ...state.loading, AllUserRoles: true },
  })),

  on(
    SearchBySkillOrConsultantApiActions.requestRolesSuccess,
    (state, action) => ({
      ...state,
      AllUserRoles: action.roles,
      loading: { ...state.loading, AllUserRoles: false },
    }),
  ),

  on(SearchBySkillOrConsultantApiActions.requestRolesError, (state) => ({
    ...state,
    loading: { ...state.loading, AllUserRoles: false },
  })),

  on(
    MyProfilePageActions.requestUpdateUserSkill,
    AddUserSkillDialogueActions.requestUpdateUserSkill,
    AddSkillToProfileActions.requestUpdateUserSkill,
    (state, action) => ({
      ...state,
      filteredConsultants: state.filteredConsultants.map((consultant) =>
        consultant.AzureId === action.azureId
          ? {
              ...consultant,
              MappedSkills: [
                ...consultant.MappedSkills.filter(
                  (skill) => skill.Id !== action.skillMapping.Id,
                ),
                action.skillMapping,
              ],
            }
          : consultant,
      ),
      loading: { ...state.loading, addUserSkill: true },
    }),
  ),

  on(
    MyProfilePageActions.requestDeleteUserSkill,
    AddSkillToProfileActions.requestDeleteUserSkill,
    (state, action) => ({
      ...state,
      filteredConsultants: state.filteredConsultants.map((consultant) =>
        consultant.AzureId === action.deleteUserSkill.AzureId
          ? {
              ...consultant,
              MappedSkills: consultant.MappedSkills.filter(
                (skill) => skill.Id !== action.deleteUserSkill.SkillId,
              ),
            }
          : consultant,
      ),
      loading: { ...state.loading, addUserSkill: true },
    }),
  ),

  on(
    MyProfilePageActions.requestDeleteMultipleUserSkills,
    AddSkillToProfileActions.requestDeleteMultipleUserSkills,
    (state, action) => ({
      ...state,
      filteredConsultants: state.filteredConsultants.map((consultant) =>
        consultant.AzureId === action.deleteUserSkills.AzureId
          ? {
              ...consultant,
              MappedSkills: consultant.MappedSkills.filter(
                (skill) =>
                  !action.deleteUserSkills.SkillIds.some((x) => x == skill.Id),
              ),
            }
          : consultant,
      ),
      loading: { ...state.loading, addUserSkill: true },
    }),
  ),

  on(
    CertificationSearchBarActions.addCertificationToSearch,
    (state, action) => ({
      ...state,
      selectedCertifications: state.selectedCertifications.find(
        (x) => x.Id === action.certification.Id,
      )
        ? state.selectedCertifications
        : [...state.selectedCertifications, action.certification],
    }),
  ),

  on(
    CertificationSearchBarActions.addAllCertificationsToSearch,
    (state, action) => ({
      ...state,
      selectedCertifications: action.certifications,
    }),
  ),

  on(
    CertificationSearchBarActions.removeCertificationFromSearch,
    (state, action) => ({
      ...state,
      selectedCertifications: state.selectedCertifications.filter(
        (cert) => cert.Id !== action.certification.Id,
      ),
    }),
  ),

  on(
    CertificationSearchBarActions.removeAllCertificationsFromSearch,
    (state) => ({
      ...state,
      selectedCertifications: [],
    }),
  ),

  on(
    SearchBySkillOrConsultantApiActions.requestOrgsSuccess,
    (state, action) => {
      const orgs = (action as any).orgs ?? (action as any).organisations ?? [];
      return {
        ...state,
        AllOrgs: orgs,
        allOrgs: orgs,
      };
    },
  ),

  on(
    SearchBySkillOrConsultantApiActions.requestLocationsSuccess,
    (state, action) => ({
      ...state,
      allLocations: action.locations,
    }),
  ),

  on(
    SearchBySkillOrConsultantPageActions.filterConsultantsByOrg,
    (state, action) => ({
      ...state,
      selectedOrganisations: action.orgs,
      filteredConsultants: [],
    }),
  ),

  on(
    SearchBySkillOrConsultantPageActions.filterConsultantsByLocation,
    (state, action) => ({
      ...state,
      selectedLocations: action.locations,
    }),
  ),

  on(
    SearchBySkillOrConsultantPageActions.lobSelectionReadyForSearch,
    (state) => ({
      ...state,
      lobSelectionReadyVersion: state.lobSelectionReadyVersion + 1,
    }),
  ),

  on(SkillApiActions.loadSearchRelatedSkillsSuccess, (state, action) => {
    const skillId = action.originalSkill?.Id;

    if (!skillId) {
      return state;
    }

    return {
      ...state,
      relatedSelectedSkills: [
        ...state.relatedSelectedSkills,
        { skillId, relatedSkills: action.relatedSkills },
      ],
    };
  }),
);

export const getConsultantSearchState =
  createFeatureSelector<IConsultantSearchState>(consultantSearchFeatureKey);

export const getFilteredConsultants = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.filteredConsultants,
);

export const getSelectedSkills = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.selectedSkills,
);

export const getSelectedIndustryItems = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.selectedIndustryItems,
);

export const getSelectedCapabilities = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.selectedCapabilities,
);

export const getSelectedCertifications = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.selectedCertifications,
);

export const getSelectedOrgs = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.selectedOrganisations,
);

export const getLobSelectionReadyVersion = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.lobSelectionReadyVersion,
);

export const getSelectedRoles = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.selectedRoles,
);

export const getSelectedLocations = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.selectedLocations,
);

export const getAnySearchTerm = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.anySearchTerm,
);

export const getConsultantNameSearchTerm = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.consultantNameSearchTerm,
);

export const getShowUnknownProficiency = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.showUnknownProficiency,
);

export const getIncludeRelatedSkills = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.includeRelatedSkills,
);

export const getIsSkillComboLogicOR = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.isSkillComboLogicOR,
);

export const getIsIndustryComboLogicOR = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.isIndustryComboLogicOR,
);

export const getIncludeExpiredCerts = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.includeExpiredCerts,
);

export const getHideNoProfile = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.hideNoProfile,
);

export const getUpdatedWithinMonths = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.updatedWithinMonths,
);

export const getOrgs = createSelector(
  getConsultantSearchState,
  (consultantSearchState) =>
    consultantSearchState.AllOrgs ?? consultantSearchState.allOrgs ?? [],
);

export const getLocations = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.allLocations,
);

export const getFilteredConsultantsLoading = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.loading.filteredConsultants,
);

export const getUserRoles = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.AllUserRoles,
);

export const getShouldShowUnknownProficiencyToggle = createSelector(
  getSelectedSkills,
  (selectedConsultantSearchSkills) =>
    !!selectedConsultantSearchSkills &&
    selectedConsultantSearchSkills.length > 0,
);

export const getResultsLoading = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.loading.filteredConsultants,
);

export const getConsultantsMatchingTeamPageFilters = createSelector(
  getFilteredConsultants,
  getSelectedSkills,
  getSelectedSkillProficiencyRange,
  getHideNoProfile,
  (
    consultants,
    selectedSkills,
    selectedSkillProficiencyRange,
    hideNoProfile,
  ) => {
    if (consultants == null) {
      return consultants;
    }

    let filteredConsultants = consultants.filter(
      (consultant) => !consultant.HasLeft,
    );

    if (hideNoProfile) {
      filteredConsultants = filteredConsultants.filter(
        (consultant) => consultant.HasProfile,
      );
    }

    if (selectedSkills != null && selectedSkills.length > 0) {
      const minProficiencyIndex =
        selectedSkillProficiencyRange != null
          ? getSkillRatingIndex(selectedSkillProficiencyRange.min)
          : -1;

      const maxRaw = selectedSkillProficiencyRange?.max;
      const maxProficiencyIndex =
        selectedSkillProficiencyRange != null
          ? maxRaw > 5 && maxRaw !== 99
            ? 5
            : getSkillRatingIndex(maxRaw)
          : -1;

      filteredConsultants = filteredConsultants.filter((consultant) => {
        let filteredConsultantSkills = consultant.MappedSkills.filter(
          (mappedSkill) =>
            selectedSkills
              .map((selectedSkill) => selectedSkill.Name)
              .includes(mappedSkill.Name),
        );

        if (minProficiencyIndex >= 0 && maxProficiencyIndex >= 0) {
          filteredConsultantSkills = filteredConsultantSkills.filter(
            (skill) => {
              const skillRatingIndex = getSkillRatingIndex(skill.SkillRating);
              return (
                skillRatingIndex >= minProficiencyIndex &&
                skillRatingIndex <= maxProficiencyIndex
              );
            },
          );
        }

        return filteredConsultantSkills.length > 0;
      });
    }

    return filteredConsultants;
  },
);

export const getSearchBySkillOrConsultantSearchCriteriaExists = createSelector(
  getSelectedSkills,
  getSelectedCapabilities,
  getSelectedCertifications,
  getSelectedOrgs,
  getAnySearchTerm,
  getConsultantNameSearchTerm,
  getSelectedRoles,
  getSelectedLocations,
  getSelectedIndustryItems,
  getUpdatedWithinMonths,
  (
    selectedSkills,
    selectedCapabilities,
    selectedCertifications,
    selectedOrganisations,
    anySearchTerm,
    consultantName,
    selectedRoles,
    selectedLocations,
    selectedIndustryItems,
    updatedWithinMonths,
  ) => {
    return (
      (selectedSkills && selectedSkills.length > 0) ||
      (selectedCapabilities && selectedCapabilities.length > 0) ||
      (selectedCertifications && selectedCertifications.length > 0) ||
      (selectedOrganisations && selectedOrganisations.length > 0) ||
      (anySearchTerm && anySearchTerm.trim() !== "") ||
      (consultantName && consultantName !== "") ||
      (selectedRoles && selectedRoles.length > 0) ||
      (selectedLocations && selectedLocations.length > 0) ||
      (selectedIndustryItems && selectedIndustryItems.length > 0) ||
      updatedWithinMonths !== null
    );
  },
);

export const getSortedFilteredConsultantsByMatchingSkills = createSelector(
  getSelectedSkills,
  getFilteredConsultants,
  getSelectedSkillProficiencyRange,
  getHideNoProfile,
  (
    selectedSkills,
    filteredConsultants,
    selectedSkillProficiencyRange,
    hideNoProfile,
  ) => {
    const safeLower = (v: unknown): string =>
      (typeof v === "string" ? v : String(v ?? "")).trim().toLowerCase();

    const firstToken = (displayName: string): string => {
      const parts = displayName.split(/\s+/).filter(Boolean);
      return (parts[0] ?? "").toLowerCase();
    };

    const combinedSkillScore = (
      skills: ISkillNodeWithRating[],
      desiredSkills: ISkillNode[],
    ) => {
      const maxScore: number = desiredSkills.length * 5;

      if (maxScore <= 0) {
        return 0;
      }

      const matchedSkills: ISkillNodeWithRating[] = skills.filter((s) =>
        desiredSkills.some((ds) => ds.Id === s.Id),
      );

      let consultantRating = matchedSkills.length > 0 ? 1 : 0;

      const accFn = (
        accumulator: number,
        currentValue: ISkillNodeWithRating,
      ) =>
        currentValue.SkillRating === SkillRating.MovedOn
          ? accumulator
          : accumulator + currentValue.SkillRating;

      consultantRating += matchedSkills.reduce(accFn, 0) / maxScore;
      return consultantRating;
    };

    let filteredProficientConsultants = filteredConsultants.filter(
      (consultant) => !consultant.HasLeft,
    );

    if (hideNoProfile) {
      filteredProficientConsultants = filteredProficientConsultants.filter(
        (consultant) => consultant.HasProfile,
      );
    }

    if (selectedSkills?.length > 0) {
      const minProficiencyIndex =
        selectedSkillProficiencyRange != null
          ? getSkillRatingIndex(selectedSkillProficiencyRange.min)
          : -1;

      const maxRaw = selectedSkillProficiencyRange?.max;
      const maxProficiencyIndex =
        selectedSkillProficiencyRange != null
          ? maxRaw > 5 && maxRaw !== 99
            ? 5
            : getSkillRatingIndex(maxRaw)
          : -1;

      filteredProficientConsultants = filteredProficientConsultants.filter(
        (consultant) => {
          let filteredConsultantSkills = consultant.MappedSkills.filter(
            (mappedSkill) =>
              selectedSkills
                .map((selectedSkill) => selectedSkill.Name)
                .includes(mappedSkill.Name),
          );

          if (minProficiencyIndex >= 0 && maxProficiencyIndex >= 0) {
            filteredConsultantSkills = filteredConsultantSkills.filter(
              (skill) => {
                const skillRatingIndex = getSkillRatingIndex(skill.SkillRating);
                return (
                  skillRatingIndex >= minProficiencyIndex &&
                  skillRatingIndex <= maxProficiencyIndex
                );
              },
            );
          }

          return filteredConsultantSkills.length > 0;
        },
      );
    }

    return filteredProficientConsultants.slice().sort((a, b) => {
      const combinedSkillScoreA = combinedSkillScore(
        a.MappedSkills,
        selectedSkills,
      );
      const combinedSkillScoreB = combinedSkillScore(
        b.MappedSkills,
        selectedSkills,
      );

      const displayNameA = safeLower(a.DisplayName);
      const displayNameB = safeLower(b.DisplayName);
      const firstNameA = firstToken(displayNameA);
      const firstNameB = firstToken(displayNameB);
      const emailA = safeLower((a as any).Email);
      const emailB = safeLower((b as any).Email);

      return (
        combinedSkillScoreB - combinedSkillScoreA ||
        firstNameA.localeCompare(firstNameB) ||
        displayNameA.localeCompare(displayNameB) ||
        emailA.localeCompare(emailB)
      );
    });
  },
);

export const getNumberOfFilteredConsultants = createSelector(
  getSortedFilteredConsultantsByMatchingSkills,
  (filteredConsultants) => {
    return filteredConsultants ? filteredConsultants.length : 0;
  },
);

export const getFilteredConsultantsWithoutSelectedConsultants = createSelector(
  getFilteredConsultants,
  getSelectedConsultants,
  (consultants, selectedConsultants) => {
    if (selectedConsultants && selectedConsultants.length >= 1) {
      const selectedConsultantsIds = selectedConsultants.map(
        (consultant) => consultant.AzureId,
      );
      return consultants.filter(
        (consultant) => !selectedConsultantsIds.includes(consultant.AzureId),
      );
    }
    return consultants;
  },
);

export const getCriteriaExistsForAny = createSelector(
  getAnySearchTerm,
  (consultantAny) => !!consultantAny && consultantAny !== "",
);

export const getCriteriaExistsForName = createSelector(
  getConsultantNameSearchTerm,
  (consultantName) => !!consultantName && consultantName !== "",
);

export const getSearchSkillRelatedSkills = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => consultantSearchState.relatedSelectedSkills,
);

export const getSearchSkillRelatedSkillsUnique = createSelector(
  getConsultantSearchState,
  (consultantSearchState) => {
    const flattenedSkills = consultantSearchState.relatedSelectedSkills.flatMap(
      (s) => s.relatedSkills,
    );

    return flattenedSkills.reduce((acc: ISkillNode[], skill: ISkillNode) => {
      if (
        !acc.find((x) => x.Id === skill.Id) &&
        !consultantSearchState.selectedSkills.find((x) => x.Id === skill.Id)
      ) {
        acc.push(skill);
      }
      return acc;
    }, []);
  },
);

export const getShouldShowIncludeRelatedSkillsToggle = createSelector(
  getSearchSkillRelatedSkillsUnique,
  (relatedSearchSkills) =>
    !!relatedSearchSkills && relatedSearchSkills.length > 0,
);
